// IRCII-style status bar - classic terminal aesthetic
// Layout:
// Left:  [* READY] [Player/Companion] | [Context-specific stats]
// Center: transient pipeline status (only while streaming)
// Right: [Credits: <micros>] [Local Time]
//
// Narrative mode: [Player] | [Chapter: N | Characters: N | Location: ...]
// Companion mode: [🤝 Name] | [Mood: ...] | [Phase: ...]

import { useCallback, useEffect, useRef, useState } from 'react';
import { useActiveWorldStore } from '../store';
import { useIsMobile } from '../hooks';

// Errors no longer surface through the status bar (2026-04-21). They render
// inline in chat via ErrorNotice — tiny truncated red text here was
// unreadable and hid the technical detail a dev needs.

function formatMicros(value: number): string {
  return value.toLocaleString('en-US');
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '…';
}

function formatLocalTime(): string {
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const tz = now.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
  return `${time} ${tz}`;
}

// Mood emoji mapping - keeps it subtle and expressive
function getMoodEmoji(mood: string | null): string {
  if (!mood) return '💭';
  const moodLower = mood.toLowerCase();
  
  // Positive moods
  if (moodLower.includes('happy') || moodLower.includes('joy')) return '😊';
  if (moodLower.includes('excit')) return '✨';
  if (moodLower.includes('playful') || moodLower.includes('mischiev')) return '😏';
  if (moodLower.includes('content') || moodLower.includes('peaceful')) return '😌';
  if (moodLower.includes('affection') || moodLower.includes('loving')) return '💕';
  if (moodLower.includes('curious') || moodLower.includes('intrig')) return '🤔';
  if (moodLower.includes('warm')) return '🌸';
  
  // Intimate/sensual moods
  if (moodLower.includes('aroused') || moodLower.includes('turned on')) return '🔥';
  if (moodLower.includes('flirt') || moodLower.includes('teas')) return '😘';
  if (moodLower.includes('desir') || moodLower.includes('wanting')) return '🥵';
  if (moodLower.includes('sensual') || moodLower.includes('seduct')) return '💋';
  if (moodLower.includes('passion') || moodLower.includes('lust')) return '🔥';
  if (moodLower.includes('intimate') || moodLower.includes('clos')) return '💗';
  if (moodLower.includes('yearn') || moodLower.includes('long')) return '💫';
  if (moodLower.includes('bliss') || moodLower.includes('ecsta')) return '✨';
  if (moodLower.includes('satisfy') || moodLower.includes('fulfill')) return '😌';
  if (moodLower.includes('afterglow')) return '🌙';
  
  // Neutral moods  
  if (moodLower.includes('calm') || moodLower.includes('neutral')) return '😐';
  if (moodLower.includes('thoughtful') || moodLower.includes('pensive')) return '💭';
  if (moodLower.includes('focused') || moodLower.includes('engaged')) return '👀';
  if (moodLower.includes('listen')) return '👂';
  
  // Negative moods
  if (moodLower.includes('sad') || moodLower.includes('melanchol')) return '😔';
  if (moodLower.includes('tired') || moodLower.includes('exhaust')) return '😴';
  if (moodLower.includes('worry') || moodLower.includes('anxious')) return '😟';
  if (moodLower.includes('frustrat') || moodLower.includes('annoy')) return '😤';
  if (moodLower.includes('tense') || moodLower.includes('nervous')) return '😬';
  if (moodLower.includes('distant') || moodLower.includes('withdrawn')) return '🫥';
  if (moodLower.includes('upset') || moodLower.includes('hurt')) return '😢';
  if (moodLower.includes('vulnerab') || moodLower.includes('open')) return '🥺';
  
  return '💭'; // Default
}

// Format relationship phase as a human-readable label
function formatRelationshipPhase(phase: string | null): string {
  if (!phase) return 'Getting to know you';
  
  const labels: Record<string, string> = {
    // Canonical phases
    'strangers': 'Just met',
    'acquaintances': 'Getting to know you',
    'building_trust': 'Building trust',
    'comfortable': 'Comfortable together',
    'close_friends': 'Close connection',
    'flirting': 'Flirting',
    'romantic_tension': 'Tension building',
    'new_lovers': 'New lovers',
    'passionate': 'Passionate',
    'deeply_intimate': 'Deep bond',
    'devoted': 'Devoted',
    'complicated': 'It\'s complicated',
    'strained': 'Strained',
    'rekindling': 'Rekindling',
    // Legacy fallbacks
    'early_days': 'Just met',
    'early_acquaintance': 'Getting to know you',
    'close': 'Close connection',
    'intimate': 'Deep bond',
    'evolving': 'It\'s complicated',
  };
  
  return labels[phase] || phase.replace(/_/g, ' ');
}

function Segment({
  children,
  title,
  className = '',
  highlight = false,
  warning = false,
  error = false,
  success = false,
  muted = false,
}: {
  children: React.ReactNode;
  title?: string;
  className?: string;
  highlight?: boolean;
  warning?: boolean;
  error?: boolean;
  success?: boolean;
  muted?: boolean;
}) {
  let colorClass = 'text-zinc-300';
  if (highlight) colorClass = 'text-cyan-400';
  if (warning) colorClass = 'text-amber-400';
  if (error) colorClass = 'text-red-400';
  if (success) colorClass = 'text-emerald-400';
  if (muted) colorClass = 'text-zinc-500';

  return (
    <span className={`status-segment inline-flex items-center flex-shrink-0 ${colorClass} ${className}`} title={title}>
      <span className="text-zinc-600">[</span>
      {children}
      <span className="text-zinc-600">]</span>
    </span>
  );
}

function Pipe() {
  return <span className="text-zinc-700 mx-1.5 flex-shrink-0">|</span>;
}

function PipelineStatus({ status }: { status: { phase: string; status: string; icon: string } }) {
  // Narrative phases: context, generation, guardrails, complete
  // Companion phases: scaffolding, preflight, generation, state, complete
  const narrativePhases = ['context', 'generation', 'guardrails', 'complete'];
  const companionPhases = ['scaffolding', 'preflight', 'generation', 'state', 'complete'];

  // Determine which pipeline we're showing
  const isCompanionPhase = companionPhases.includes(status.phase) && !['generation', 'complete'].includes(status.phase);
  const phases = isCompanionPhase || status.phase === 'scaffolding' || status.phase === 'preflight' || status.phase === 'state'
    ? companionPhases
    : narrativePhases;
  const currentIndex = phases.indexOf(status.phase);

  const phaseLabels: Record<string, string> = {
    // Narrative
    context: 'CTX',
    generation: 'GEN',
    guardrails: 'CHK',
    // Companion
    scaffolding: 'INIT',
    preflight: 'PLAN',
    state: 'UPD',
    // Shared
    complete: 'DONE',
    postprocessing: 'POST',
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        {phases.slice(0, -1).map((phase, i) => {
          const isActive = i === currentIndex;
          const isComplete = i < currentIndex;
          return (
            <span
              key={phase}
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                isActive
                  ? 'bg-amber-500/30 text-amber-300 animate-pulse'
                  : isComplete
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-zinc-700/50 text-zinc-500'
              }`}
            >
              {phaseLabels[phase] || phase.toUpperCase().slice(0, 3)}
            </span>
          );
        })}
      </div>
      <span className="text-amber-400 text-xs">{status.status}</span>
    </div>
  );
}

export function StatusBar() {
  const isConnected = useActiveWorldStore((state) => state.isConnected);
  const isStreaming = useActiveWorldStore((state) => state.isStreaming);
  const simulationStatus = useActiveWorldStore((state) => state.simulationStatus);
  const worldState = useActiveWorldStore((state) => state.worldState);
  const creditsBalanceMicros = useActiveWorldStore((state) => state.creditsBalanceMicros);
  const companionState = useActiveWorldStore((state) => state.companionState);
  const isMobile = useIsMobile();

  const leftRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(formatLocalTime());
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(formatLocalTime());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Smart label sizing — abbreviate labels when left section is tight
  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      setCompact(entry.contentRect.width < 600);
    }
  }, []);

  useEffect(() => {
    const el = leftRef.current;
    if (!el) return;
    const observer = new ResizeObserver(handleResize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleResize]);

  // Determine simulation type
  const simulationType = worldState?.world?.simulation_type ?? 'narrative';
  const isCompanion = simulationType === 'companion';

  // Narrative-specific state
  const seed = worldState?.seed;
  const activeCharacters = worldState?.activeCharacters || [];
  const playerCharacter = activeCharacters.find((c) => c.is_player_character);
  const characterCount = activeCharacters.length;
  // Read chapter number from the dedicated store slice — it's populated
  // from getWorld() on load, world_state payload on connect, and
  // chapter_change WS pushes. worldState.currentChapter is always null
  // after the WorldPage initializer so reading from there falls through
  // to "1" forever.
  const currentChapterNumber = useActiveWorldStore((state) => state.currentChapterNumber);
  const chapterNumber = currentChapterNumber ?? 1;
  // Prefer player character's dynamically-updated location, fall back to seed
  const currentLocation = playerCharacter?.current_location ?? seed?.current_location;

  // Companion-specific state
  const companionProfile = worldState?.companionProfile;
  const companionName = companionProfile?.name ?? 'Companion';
  const mood = companionState?.mood ?? null;
  const moodEmoji = getMoodEmoji(mood);
  const relationshipPhase = companionState?.relationshipPhase ?? null;

  const getConnectionIndicator = (): { symbol: string; label: string; className: string } => {
    if (!isConnected) {
      return { symbol: '○', label: 'OFFLINE', className: 'text-red-500' };
    }
    if (isStreaming && !simulationStatus) {
      return { symbol: '◐', label: 'WORKING', className: 'text-amber-400 animate-pulse' };
    }
    if (isStreaming && simulationStatus) {
      return { symbol: '◐', label: '', className: 'text-amber-400' };
    }
    return { symbol: '*', label: 'READY', className: 'text-emerald-500' };
  };

  const connection = getConnectionIndicator();

  // === MOBILE LAYOUT: minimal single-row status ===
  if (isMobile) {
    const identityName = isCompanion ? companionName : playerCharacter?.name;
    return (
      <div className="status-bar relative shrink-0 min-w-0 h-8 px-2 flex items-center bg-zinc-900/95 border-t border-zinc-800 font-mono text-[10px] select-none overflow-hidden whitespace-nowrap">
        {/* Connection + Identity */}
        <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
          <Segment title={`Connection: ${connection.label || 'Processing'}`} className={connection.className}>
            <span className={connection.className}>{connection.symbol}</span>
            {connection.label && <span className="ml-1">{connection.label}</span>}
          </Segment>

          {identityName && (
            <Segment title={identityName} highlight={!isCompanion}>
              {isCompanion && <span className="text-pink-400 mr-1">💬</span>}
              <span className="truncate max-w-[7rem]">{identityName}</span>
            </Segment>
          )}
        </div>

        {/* Pipeline status text (when streaming) */}
        {isStreaming && simulationStatus && (
          <span className="text-amber-400 ml-2 flex-shrink-0 truncate max-w-[10rem]">
            {simulationStatus.status}
          </span>
        )}
      </div>
    );
  }

  // === DESKTOP LAYOUT: full status bar (unchanged) ===
  return (
    <div className="status-bar relative shrink-0 min-w-0 h-8 px-3 flex items-center bg-zinc-900/95 border-t border-zinc-800 font-mono text-xs select-none overflow-hidden whitespace-nowrap">
      {/* === LEFT SECTION: Identity & Stats === */}
      <div ref={leftRef} className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
        <Segment title={`Connection: ${connection.label || 'Processing'}`} className={connection.className}>
          <span className={connection.className}>{connection.symbol}</span>
          {connection.label && <span className="ml-1">{connection.label}</span>}
        </Segment>

        {/* Companion Mode: Show companion identity, mood, and relationship */}
        {isCompanion && (
          <>
            <Segment title={`Chatting with ${companionName}`} highlight>
              <span className="text-pink-400">💬</span>
              <span className="ml-1 truncate max-w-[8rem]" title={companionName}>{companionName}</span>
            </Segment>

            <Pipe />

            {/* Current mood with emoji */}
            <Segment
              muted
              title={mood ? `Current mood: ${mood}` : 'Mood not yet expressed'}
            >
              <span className="mr-1">{moodEmoji}</span>
              <span className="text-zinc-300 capitalize">
                {mood ? truncate(mood, 25) : 'thinking...'}
              </span>
            </Segment>

            <Pipe />

            {/* Relationship phase */}
            <Segment
              muted
              title={`Relationship: ${formatRelationshipPhase(relationshipPhase)}`}
            >
              <span className="text-zinc-400">🤝</span>
              <span className="text-zinc-300 ml-1 truncate max-w-[10rem]" title={formatRelationshipPhase(relationshipPhase)}>
                {formatRelationshipPhase(relationshipPhase)}
              </span>
            </Segment>
          </>
        )}

        {/* Narrative Mode: Show player and world stats */}
        {!isCompanion && (
          <>
            {playerCharacter && (
              <Segment title={`Playing as ${playerCharacter.name}`} highlight>
                <span className="truncate max-w-[8rem]" title={playerCharacter.name}>{playerCharacter.name}</span>
              </Segment>
            )}

            <Pipe />

            <Segment
              muted
              title={`Chapter ${chapterNumber} | Characters: ${characterCount}${currentLocation ? ` | Location: ${currentLocation}` : ''}`}
            >
              <span className="text-zinc-400">{compact ? 'Ch.' : 'Chapter:'}</span>
              <span className="text-zinc-200 ml-1">{chapterNumber}</span>

              <span className="text-zinc-600 mx-2">|</span>

              <span className="text-zinc-400">{compact ? 'NPCs:' : 'Characters:'}</span>
              <span className="text-zinc-200 ml-1">{characterCount}</span>

              <span className="text-zinc-600 mx-2">|</span>

              <span className="text-zinc-400">{compact ? 'Loc:' : 'Location:'}</span>
              <span className="text-zinc-200 ml-1 truncate max-w-[14rem]" title={currentLocation ?? undefined}>
                {currentLocation ?? 'Unknown'}
              </span>
            </Segment>
          </>
        )}
      </div>

      {/* === CENTER SECTION: Pipeline Status (transient) === */}
      <div className="flex items-center justify-center mx-4 flex-shrink-0">
        {isStreaming && simulationStatus && <PipelineStatus status={simulationStatus} />}
      </div>

      {/* === RIGHT SECTION: Credits + Time === */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {creditsBalanceMicros !== null && (
          <Segment title="Credits balance (micros)" highlight>
            <span className="text-zinc-400">Credits:</span>
            <span className="text-cyan-300 ml-1">{formatMicros(creditsBalanceMicros)}</span>
          </Segment>
        )}

        <Segment title="Local time" muted>
          {currentTime}
        </Segment>
      </div>
    </div>
  );
}
